/**
 * useContactSelection
 * 
 * Shared hook for fetching team members and managing multi-select contact selection.
 * Used by NewConversationDialog and QuickComposeModal.
 */
import { useState, useEffect, useMemo, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function useContactSelection({ open, user, preSelectedUsers = [] }) {
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Ref to the search input so we can keep focus after a pick (rapid multi-add).
  // Optional for consumers — if not attached, focus() is a safe no-op.
  const searchInputRef = useRef(null);

  // Fetch team members when open
  useEffect(() => {
    if (!open || !user?.church_id) return;

    const fetchTeamMembers = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_URL}/api/user/team/${user.church_id}`);
        if (response.ok) {
          const data = await response.json();
          const others = (data.members || []).filter(m => m.id !== user.id);
          setTeamMembers(others);
        } else {
          throw new Error('Failed to fetch team members');
        }
      } catch (err) {
        console.error('Error fetching team members:', err);
        setError('Could not load team members');
        setTeamMembers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamMembers();
  }, [open, user?.church_id, user?.id]);

  // Set pre-selected users once team members load
  useEffect(() => {
    if (preSelectedUsers.length > 0 && teamMembers.length > 0 && selectedUsers.length === 0) {
      const matched = teamMembers.filter(m =>
        preSelectedUsers.some(p => p.id === m.id)
      );
      if (matched.length > 0) setSelectedUsers(matched);
    }
  }, [preSelectedUsers, teamMembers, selectedUsers.length]);

  // Reset state when closed
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setSelectedUsers([]);
      setError(null);
    }
  }, [open]);

  const filteredMembers = useMemo(() => {
    if (!searchQuery.trim()) return teamMembers;
    const q = searchQuery.toLowerCase();
    return teamMembers.filter(m => {
      const name = (m.name || m.email || '').toLowerCase();
      return name.includes(q);
    });
  }, [teamMembers, searchQuery]);

  const toggleUser = (member) => {
    // Decide the side-effect from the current (pre-update) selection.
    const wasSelected = selectedUsers.some(u => u.id === member.id);
    // Toggle off `prev` (fresh state) so a rapid double-click can never produce a
    // duplicate recipient — the updater stays the single source of truth.
    setSelectedUsers(prev => {
      const exists = prev.some(u => u.id === member.id);
      return exists ? prev.filter(u => u.id !== member.id) : [...prev, member];
    });
    // On ADD: reset the search so the field is clear and ready for the next name,
    // and keep focus on it so the user can keep typing without re-clicking. We
    // only reset on add (not remove) so removing a chip mid-search doesn't wipe
    // what the user is currently typing. (If a stale double-click misjudges this,
    // it just clears an already-empty search + refocuses — harmless.)
    if (!wasSelected) {
      setSearchQuery('');
      searchInputRef.current?.focus();
    }
  };

  const isSelected = (memberId) => selectedUsers.some(u => u.id === memberId);

  return {
    teamMembers,
    selectedUsers,
    searchQuery,
    setSearchQuery,
    searchInputRef,
    loading,
    error,
    filteredMembers,
    toggleUser,
    isSelected,
  };
}
